// recovery-reconciler.ts
//
// Sửa ĐUÔI DỞ của journal theo §3.4 — máy trạng thái điều khiển bằng MANIFEST, nên
// bị ngắt giữa chừng vẫn resume đúng chỗ:
//
//   phase=saved      → tail đã lưu forensic (bytes + sha256 + offset + lastSeq)
//   phase=truncated  → file đã ftruncate về lastValidOffset + fsync fd + fsync dir
//   phase=done       → đã append `journal_recovered` đúng MỘT lần
//
// Bất biến cứng:
//  • CHỈ `partial_tail` mới được xử lý ở đây. `corrupt` ⇒ RECOVERING, người quyết.
//  • LƯU tail TRƯỚC khi truncate. Không có bằng chứng nào chứng minh nội dung đuôi
//    từng được chấp nhận (receipt/projection) thì mới được cắt.
//  • Không bao giờ append record mới lên trên một đuôi hỏng.

import { createHash } from "node:crypto";
import { promises as fs } from "node:fs";
import { dirname, join } from "node:path";

import { ensureDir } from "../filesystem-helpers.js";
import { type DurableFsOps, realDurableFsOps, writeFileDurable } from "./durable-file-writer.js";
import type { JournalScanReport } from "./journal-scan-report.js";
import {
  manifestPathFor,
  type RecoveryManifest,
  readManifest,
  recoveryDir,
  writeManifest,
} from "./recovery-manifest-io.js";

export type { RecoveryManifest, RecoveryPhase } from "./recovery-manifest-io.js";
export { readRecoveryManifest } from "./recovery-manifest-io.js";

export interface RecoveryOutcome {
  performed: boolean;
  manifestPath?: string;
  manifest?: RecoveryManifest;
}

export interface RecoveryDeps {
  ops?: DurableFsOps;
  now?: () => Date;
  /** Bằng chứng đuôi thiếu ĐÃ từng được chấp nhận (receipt/projection vượt seq). */
  tailWasAcknowledged?: (report: JournalScanReport) => Promise<boolean>;
}

export class RecoveryBlockedError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "RecoveryBlockedError";
  }
}

/**
 * Chạy recovery cho `partial_tail`. IDEMPOTENT: gọi lại sau khi bị ngắt sẽ tiếp tục
 * từ `phase` trong manifest, không lặp lại việc đã làm.
 *
 * `appendRecoveredEvent` do caller cung cấp (mission-store dùng journal của chính nó)
 * — reconciler không tự mở journal để tránh hai writer.
 */
export async function reconcilePartialTail(
  journalPath: string,
  report: JournalScanReport,
  appendRecoveredEvent: (manifest: RecoveryManifest) => Promise<void>,
  deps: RecoveryDeps = {},
): Promise<RecoveryOutcome> {
  if (report.status !== "partial_tail" || report.tail === undefined) {
    return { performed: false };
  }
  const ops = deps.ops ?? realDurableFsOps;
  const now = deps.now ?? (() => new Date());

  if (deps.tailWasAcknowledged && (await deps.tailWasAcknowledged(report))) {
    throw new RecoveryBlockedError(
      "Đuôi journal thiếu nhưng có bằng chứng nội dung đó ĐÃ được chấp nhận " +
        "(receipt/projection). Không phân biệt được 'write dở' với 'mất dữ liệu' ⇒ " +
        "giữ RECOVERING cho người xem xét (§3.4).",
    );
  }

  const dir = recoveryDir(journalPath);
  await ensureDir(dir);
  const manifestPath = manifestPathFor(journalPath, report.lastSeq, report.lastValidOffset);
  const tailFile = join(dir, `${report.lastSeq}-${report.lastValidOffset}.tail.bin`);
  let manifest = await readManifest(manifestPath);

  // PHASE 1 — lưu forensic TRƯỚC khi đụng vào journal.
  if (manifest === undefined) {
    await writeFileDurable(tailFile, report.tail, { ops });
    manifest = {
      offset: report.lastValidOffset,
      sha256: createHash("sha256").update(report.tail).digest("hex"),
      lastSeq: report.lastSeq,
      phase: "saved",
      tailBytes: report.tail.length,
      tailFile,
      startedAt: now().toISOString(),
    };
    await writeManifest(manifestPath, manifest, ops);
  }

  // PHASE 2 — truncate + fsync fd + fsync dir.
  if (manifest.phase === "saved") {
    const handle = await ops.open(journalPath, "r+");
    try {
      await handle.truncate(manifest.offset);
      await ops.fsyncFile(handle);
    } finally {
      await ops.close(handle);
    }
    await ops.fsyncDir(dirname(journalPath));
    manifest = { ...manifest, phase: "truncated" };
    await writeManifest(manifestPath, manifest, ops);
  }

  // PHASE 3 — append `journal_recovered` đúng MỘT lần.
  if (manifest.phase === "truncated") {
    await appendRecoveredEvent(manifest);
    manifest = { ...manifest, phase: "done" };
    await writeManifest(manifestPath, manifest, ops);
  }

  return { performed: true, manifestPath, manifest };
}

/** Manifest still pending for `journalPath`, or undefined when none/finished. */
async function findPendingManifest(
  journalPath: string,
): Promise<{ path: string; manifest: RecoveryManifest } | undefined> {
  let names: string[];
  try {
    names = await fs.readdir(recoveryDir(journalPath));
  } catch {
    return undefined; // no recovery dir => nothing was ever started
  }
  for (const name of names.sort()) {
    if (!name.endsWith(".manifest.json")) continue;
    const path = join(recoveryDir(journalPath), name);
    const manifest = await readManifest(path);
    if (manifest && manifest.phase !== "done") return { path, manifest };
  }
  return undefined;
}

export type PendingRecoveryResume =
  | { kind: "none" }
  | { kind: "completed"; manifest: RecoveryManifest }
  | { kind: "blocked"; reason: string };

/**
 * Audit 2026-09-14 (M2): finish a recovery that was interrupted between phases,
 * in the case `reconcilePartialTail` cannot see.
 *
 * That function returns early unless the scan says `partial_tail`. But once
 * PHASE 2 has truncated the journal, the file scans CLEAN — so a process that
 * died right after the truncate left the manifest at `truncated` with
 * `journal_recovered` never appended, and no later open ever looked again. The
 * manifest stayed pending forever.
 *
 * Only `truncated` is resumed automatically: PHASE 3 just appends one event and
 * touches no existing bytes.
 *
 * `saved` is deliberately NOT resumed (D3). At that phase the tail is saved but
 * we cannot tell whether PHASE 2 ran: `scanJournal` reports the file's CURRENT
 * state only, so "truncate happened and valid events were appended afterwards"
 * looks exactly like "truncate never happened". Truncating to the manifest
 * offset on that guess would delete real events — the precise failure this
 * subsystem exists to prevent — so it is handed to a human instead.
 */
export async function resumePendingRecovery(
  journalPath: string,
  appendRecoveredEvent: (manifest: RecoveryManifest) => Promise<void>,
  deps: RecoveryDeps = {},
): Promise<PendingRecoveryResume> {
  const pending = await findPendingManifest(journalPath);
  if (pending === undefined) return { kind: "none" };

  if (pending.manifest.phase === "saved") {
    return {
      kind: "blocked",
      reason:
        `Recovery dở ở phase "saved" (manifest ${pending.path}) nhưng journal hiện quét CLEAN — ` +
        "không phân biệt được 'đã truncate rồi có event mới' với 'chưa truncate bao giờ'. " +
        "Tự cắt theo offset cũ sẽ xoá event hợp lệ ⇒ giữ RECOVERING cho người xem xét (§3.4).",
    };
  }

  const ops = deps.ops ?? realDurableFsOps;
  await appendRecoveredEvent(pending.manifest);
  const manifest: RecoveryManifest = { ...pending.manifest, phase: "done" };
  await writeManifest(pending.path, manifest, ops);
  return { kind: "completed", manifest };
}
